/**
 * The Add row and Edit row forms (infrastructure/db-helper/DESIGN_EDITING.md console flows 4 to 6): a table's info,
 * one field per column, and the write each form sends.
 *
 * ⚠️ NO IMPORTS, ON PURPOSE (dataView.ts says why).
 *
 * ⛔ VALUES ARE THE CUSTOMER'S. A field's text is sent as typed; old values are the text the page showed (or the whole
 *    cell, loaded), never a cut cell's "…[cut]" text. Nothing here stores them.
 */

// ── The info answer (backend dataView.ts, the info shape) ────────────────────────────────────────────────────────

export interface InfoColumn {
    name: string;
    type: string;
    not_null: boolean;
    default: string | null;
    identity: 'always' | 'by_default' | null;
    generated: boolean;
    in_key: boolean;
}

/** E27: one foreign key of the table, its columns and the referenced ones in the constraint's order. */
export interface ForeignKey { name: string; columns: string[]; ref_schema: string; ref_table: string; ref_columns: string[] }

export interface TableInfo {
    schema: string;
    table: string;
    kind: string;
    key: string[];
    columns: InfoColumn[];
    can: { insert: boolean; update: boolean; delete: boolean; alter: boolean; export: boolean };
    /** E27 (helper 1.4.0); empty from an older build */
    foreignKeys: ForeignKey[];
    /** E30 (helper 1.4.1): row-level security, read only; null from an older build */
    rls: RowSecurity | null;
}

/** E30: one row-level security policy, as Postgres keeps it (the expressions are the customer's own SQL text). */
export interface RlsPolicy { name: string; command: string; permissive: boolean; roles: string[]; using: string | null; check: string | null }
export interface RowSecurity { enabled: boolean; forced: boolean; policies: RlsPolicy[] }

/** The info answer's rls, or null when it is absent or not that shape. */
const readRowSecurity = (data: unknown): RowSecurity | null => {
    const r = data as any;
    if (!r || typeof r.enabled !== 'boolean' || typeof r.forced !== 'boolean' || !Array.isArray(r.policies)) return null;
    const policies: RlsPolicy[] = [];
    for (const p of r.policies) {
        if (!p || typeof p.name !== 'string' || typeof p.command !== 'string' || typeof p.permissive !== 'boolean' || !strings(p.roles, 0)
            || !(p.using === null || typeof p.using === 'string') || !(p.check === null || typeof p.check === 'string')) return null;
        policies.push({ name: p.name, command: p.command, permissive: p.permissive, roles: p.roles, using: p.using, check: p.check });
    }
    return { enabled: r.enabled, forced: r.forced, policies };
};

const strings = (v: unknown, min: number): v is string[] => Array.isArray(v) && v.length >= min && v.every((x) => typeof x === 'string');

/** The info answer's foreign keys; one that is not whole, or names a column the table does not have, is left out. */
const readForeignKeys = (data: unknown, columns: InfoColumn[]): ForeignKey[] => {
    if (!Array.isArray(data)) return [];
    const out: ForeignKey[] = [];
    for (const f of data as any[]) {
        if (!f || typeof f.name !== 'string' || typeof f.ref_schema !== 'string' || typeof f.ref_table !== 'string') continue;
        if (!strings(f.columns, 1) || !strings(f.ref_columns, 1) || f.columns.length !== f.ref_columns.length) continue;
        if (!f.columns.every((n: string) => columns.some((c) => c.name === n))) continue;
        out.push({ name: f.name, columns: f.columns, ref_schema: f.ref_schema, ref_table: f.ref_table, ref_columns: f.ref_columns });
    }
    return out;
};

/** GET .../info, or null when the answer is not that shape (the edit controls then stay off). */
export const readInfo = (data: any): TableInfo | null => {
    if (!data || typeof data.schema !== 'string' || typeof data.table !== 'string' || !Array.isArray(data.columns)) return null;
    if (!Array.isArray(data.key) || !data.key.every((k: unknown) => typeof k === 'string')) return null;
    const can = data.can;
    if (!can || !['insert', 'update', 'delete', 'alter', 'export'].every((k) => typeof can[k] === 'boolean')) return null;
    const columns: InfoColumn[] = [];
    for (const c of data.columns) {
        if (!c || typeof c.name !== 'string' || typeof c.type !== 'string' || typeof c.not_null !== 'boolean'
            || !(c.default === null || typeof c.default === 'string') || !(c.identity === null || c.identity === 'always' || c.identity === 'by_default')
            || typeof c.generated !== 'boolean' || typeof c.in_key !== 'boolean') return null;
        columns.push({ name: c.name, type: c.type, not_null: c.not_null, default: c.default, identity: c.identity, generated: c.generated, in_key: c.in_key });
    }
    if (!data.key.every((k: string) => columns.some((c) => c.name === k && c.in_key))) return null;
    return {
        schema: data.schema, table: data.table, kind: typeof data.kind === 'string' ? data.kind : 'r', key: data.key, columns,
        can: { insert: can.insert, update: can.update, delete: can.delete, alter: can.alter, export: can.export },
        foreignKeys: readForeignKeys(data.foreign_keys, columns),
        rls: readRowSecurity(data.rls),
    };
};

/** E27: the foreign key a column belongs to (the first, when it is in several), or null. */
export const foreignKeyOf = (info: TableInfo | null, column: string): ForeignKey | null =>
    info?.foreignKeys.find((f) => f.columns.includes(column)) ?? null;

/**
 * E27: the referenced columns and the values a row holds for a foreign key, or null when the row cannot name the
 * referenced row (a column missing from the page, a NULL, or a cut cell).
 */
export const relatedValues = (fk: ForeignKey, page: Pick<KeyedPage, 'columns' | 'rows' | 'cut'>, r: number): { columns: string[]; values: string[] } | null => {
    const values: string[] = [];
    for (const name of fk.columns) {
        const c = page.columns.indexOf(name);
        const v = c >= 0 ? page.rows[r]?.[c] : undefined;
        if (v === undefined || v === null || page.cut.some(([a, b]) => a === r && b === c)) return null;
        values.push(v);
    }
    return { columns: fk.ref_columns, values };
};

// ── One field per column ─────────────────────────────────────────────────────────────────────────────────────────

export type FieldKind = 'yesno' | 'date' | 'datetime' | 'datetime_nozone' | 'json' | 'textarea' | 'input';

/** The input a column gets, from Postgres's format_type name. */
export const fieldKind = (type: string): FieldKind => {
    const t = type.toLowerCase();
    if (t.endsWith('[]')) return 'input';
    if (t === 'boolean') return 'yesno';
    if (t === 'date') return 'date';
    if (t.startsWith('timestamp') && t.endsWith(' with time zone')) return 'datetime';
    if (t.startsWith('timestamp')) return 'datetime_nozone';
    if (t === 'json' || t === 'jsonb') return 'json';
    if (t === 'text' || t.startsWith('character varying') || t.startsWith('character(') || t === 'character') return 'textarea';
    return 'input';
};

/** A type in plain words (E22); a type with no plain word keeps Postgres's own name. */
export const typeWord = (type: string): string => {
    const t = type.toLowerCase();
    if (t.endsWith('[]')) return 'List';
    if (['smallint', 'integer', 'bigint'].includes(t)) return 'Number';
    if (t.startsWith('numeric') || t === 'real' || t === 'double precision') return 'Decimal';
    if (t === 'boolean') return 'Yes/No';
    if (t === 'date') return 'Date';
    if (t.startsWith('timestamp')) return 'Date and time';
    if (t.startsWith('time')) return 'Time';
    if (t === 'json' || t === 'jsonb') return 'JSON';
    if (t === 'bytea') return 'Bytes';
    if (t === 'text' || t.startsWith('character')) return 'Text';
    return type;
};

/** The database fills this column itself and refuses a value for it (column_not_writable). */
export const filledByDatabase = (c: InfoColumn): boolean => c.generated || c.identity === 'always';

/** The database gives this column a value when none is sent: an identity, a generated column, or a sequence. */
export const isAuto = (c: InfoColumn): boolean => c.identity !== null || c.generated || /^nextval\(/i.test(c.default ?? '');

/** A value must be sent: not null, and nothing would fill it. */
export const isRequired = (c: InfoColumn): boolean => c.not_null && c.default === null && c.identity === null && !c.generated;

export const fieldLabel = (c: InfoColumn): string => (isRequired(c) ? `${c.name} *` : c.name);
export const fieldPlaceholder = (c: InfoColumn): string => (isAuto(c) ? 'Auto' : typeWord(c.type));

export const NOT_JSON = 'Not valid JSON.';

/** A datetime-local value ("2026-10-24T10:30") with the browser's offset on that date ("2026-10-24T10:30:00+02:00"). */
export const withOffset = (local: string, offsetMinutes: number): string => {
    const base = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local) ? `${local}:00` : local;
    const sign = offsetMinutes < 0 ? '-' : '+';
    const abs = Math.abs(offsetMinutes);
    return `${base}${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
};

/** The browser's offset east of UTC, in minutes, at that local time. */
export const browserOffset = (local: string): number => {
    const d = new Date(local);
    return Number.isFinite(d.getTime()) ? -d.getTimezoneOffset() : 0;
};

const isJson = (s: string): boolean => {
    try { JSON.parse(s); return true; } catch { return false; }
};

// ── Add row (flow 4) ─────────────────────────────────────────────────────────────────────────────────────────────

export type InsertBuild =
    | { ok: true; columns: string[]; row: (string | { default: true })[] }
    | { ok: false; column: string; line: string };

/**
 * The insert an Add row form sends. A field left empty is left out, so its column takes its default, or NULL. With
 * every field empty the first column is sent as DEFAULT, which inserts a row of defaults.
 */
export const buildInsert = (info: TableInfo, values: Record<string, string>, offsetFor: (local: string) => number = browserOffset): InsertBuild => {
    const columns: string[] = [];
    const row: (string | { default: true })[] = [];
    for (const c of info.columns) {
        if (filledByDatabase(c)) continue;
        const v = values[c.name] ?? '';
        if (v === '') continue;
        const kind = fieldKind(c.type);
        if (kind === 'json' && !isJson(v)) return { ok: false, column: c.name, line: NOT_JSON };
        columns.push(c.name);
        row.push(kind === 'datetime' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(v) ? withOffset(v, offsetFor(v)) : v);
    }
    if (columns.length === 0 && info.columns.length > 0) return { ok: true, columns: [info.columns[0].name], row: [{ default: true }] };
    return { ok: true, columns, row };
};

// ── Edit row (flow 5) ────────────────────────────────────────────────────────────────────────────────────────────

/** One field of the Edit row form: its text, and whether it stands for NULL. */
export interface EditField { text: string; isNull: boolean }

export const initialField = (value: string | null): EditField => (value === null ? { text: '', isNull: true } : { text: value, isNull: false });

export type UpdateBuild =
    | { ok: true; changes: { column: string; old: string | null; new: string | null }[] }
    | { ok: false; column: string; line: string };

/**
 * The changes an Edit row form sends: only the fields that were changed, each with its old text. Without Expert mode,
 * an emptied field on a column that allows NULL is NULL; with it, the field's own NULL toggle says.
 */
export const buildUpdate = (
    info: TableInfo, originals: Record<string, string | null>, fields: Record<string, EditField>, expert: boolean,
): UpdateBuild => {
    const changes: { column: string; old: string | null; new: string | null }[] = [];
    for (const c of info.columns) {
        if (c.in_key || filledByDatabase(c) || !(c.name in originals) || !(c.name in fields)) continue;
        const old = originals[c.name];
        const f = fields[c.name];
        const start = initialField(old);
        if (f.text === start.text && f.isNull === start.isNull) continue;
        const next = expert ? (f.isNull ? null : f.text) : (f.text === '' && !c.not_null ? null : f.text);
        if (next === old) continue;
        if (next !== null && fieldKind(c.type) === 'json' && !isJson(next)) return { ok: false, column: c.name, line: NOT_JSON };
        changes.push({ column: c.name, old, new: next });
    }
    return { ok: true, changes };
};

// ── A page's rows ────────────────────────────────────────────────────────────────────────────────────────────────

export interface KeyedPage { columns: string[]; rows: (string | null)[][]; key: string[]; cut: [number, number][] }

export const isCut = (page: Pick<KeyedPage, 'cut'>, r: number, c: number): boolean => page.cut.some(([a, b]) => a === r && b === c);

/** A row's key, one text per key column in the key's order; null with no key, or a key cell that is NULL or cut. */
export const rowKey = (page: KeyedPage, r: number): string[] | null => {
    if (page.key.length === 0) return null;
    const out: string[] = [];
    for (const k of page.key) {
        const c = page.columns.indexOf(k);
        const v = c >= 0 ? page.rows[r]?.[c] : undefined;
        if (v === undefined || v === null || isCut(page, r, c)) return null;
        out.push(v);
    }
    return out;
};

// ── A cell edited in the grid (E26) ──────────────────────────────────────────────────────────────────────────────

/** Text longer than this, or with a line break, keeps the Edit row form. */
export const INLINE_TEXT_MAX = 500;

export type CellEditKind = 'text' | 'yesno';

/**
 * Whether a cell edits in place, and how: only a cell the Edit row form could change (a keyed row, a column that is not
 * in the key, not filled by the database, not JSON, a cell that is not cut), and not long or multi-line text.
 */
export const cellEditKind = (info: TableInfo, page: KeyedPage, r: number, c: number): CellEditKind | null => {
    if (rowKey(page, r) === null || isCut(page, r, c)) return null;
    const name = page.columns[c];
    if (name === undefined || page.columns.indexOf(name) !== c) return null;   // a repeated column name: the form decides
    const col = info.columns.find((x) => x.name === name);
    if (!col || col.in_key || filledByDatabase(col)) return null;
    const kind = fieldKind(col.type);
    if (kind === 'json') return null;
    if (kind === 'yesno') return 'yesno';
    const v = page.rows[r]?.[c];
    if (typeof v === 'string' && (v.includes('\n') || v.length > INLINE_TEXT_MAX)) return null;
    return 'text';
};

/** The change a cell's edit sends: the Edit row form's own rule, so an emptied cell on a column that allows NULL is NULL. */
export const cellUpdate = (info: TableInfo, column: string, old: string | null, text: string): UpdateBuild =>
    buildUpdate(info, { [column]: old }, { [column]: { text, isNull: false } }, false);

/** The confirm a delete opens, every field set (ConfirmContext keeps an omitted field from the previous call). */
export const deleteRowsConfirm = (n: number) => ({
    title: n === 1 ? 'Delete row' : 'Delete rows',
    message: n === 1 ? 'Delete this row?' : `Delete ${n} rows?`,
    confirmText: 'Delete', cancelText: 'Cancel', type: 'danger' as const,
});
